import { useEffect, useState } from 'react';

/**
 * `useMediaQuery` — subscribe to a CSS media query and re-render when it flips. SSR/test-safe: when
 * `window.matchMedia` is unavailable (or a query never matches), it falls back to `false` and never
 * throws. Pure presentational state — no engine/store coupling.
 */
// Defensive: `matchMedia` may be missing (SSR) or, in tests, a reset mock that returns `undefined`
// (e.g. after `vi.restoreAllMocks()` strips a mock implementation). Returns a usable MediaQueryList or
// `null` so callers never throw and can fall back to `false` (= the query doesn't match → desktop).
function queryList(query: string): MediaQueryList | null {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return null;
  const mql = window.matchMedia(query) as MediaQueryList | undefined;
  return mql && typeof mql.matches === 'boolean' ? mql : null;
}

export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => queryList(query)?.matches ?? false);

  useEffect(() => {
    const mql = queryList(query);
    if (!mql) return;
    const onChange = () => setMatches(mql.matches);
    // Sync once on mount in case the query changed between the initial render and the effect.
    onChange();
    mql.addEventListener?.('change', onChange);
    return () => mql.removeEventListener?.('change', onChange);
  }, [query]);

  return matches;
}

/**
 * The builder chrome's three responsive MODES — a faithful clone of Shopify's theme editor (verified
 * against real Shopify screenshots). These are the single source of truth shared by the JS layout gate
 * (`data-mode` on `.bld`) and the CSS breakpoints, so the two can never drift:
 *
 *   - `desktop` (≥ 1024px) — TWO columns: a LEFT column (section tree + inspector stacked) and the
 *     PREVIEW filling the remaining width (Shopify parity; the inspector is not a third column).
 *   - `tablet`  (768–1023px) — the left rail stays a NORMAL sidebar; the inspector becomes a BOTTOM
 *     SHEET over the preview when a section is selected.
 *   - `mobile`  (< 768px) — the preview is full-bleed (edge-to-edge, no card chrome); a SINGLE bottom
 *     sheet shows the active panel (sections tree / inspector / theme / apps).
 *
 * The two breakpoints (1023.98 / 767.98px) match shell.css exactly.
 */
export type BuilderBreakpoint = 'desktop' | 'tablet' | 'mobile';

const TABLET_MAX = '(max-width: 1023.98px)';
const MOBILE_MAX = '(max-width: 767.98px)';

export function useBuilderBreakpoint(): BuilderBreakpoint {
  // Two nested queries: ≤1023.98 is tablet-or-narrower, ≤767.98 is mobile. Above both → desktop.
  const isTabletOrNarrower = useMediaQuery(TABLET_MAX);
  const isMobile = useMediaQuery(MOBILE_MAX);
  if (isMobile) return 'mobile';
  if (isTabletOrNarrower) return 'tablet';
  return 'desktop';
}

/**
 * Convenience: `true` for tablet OR mobile (i.e. anything below the desktop 3-panel layout). Used where
 * the shell only needs "is this a reduced layout?" without caring which of the two it is.
 */
export function useIsNarrow(): boolean {
  return useBuilderBreakpoint() !== 'desktop';
}
